import { Typography } from 'antd';
import type React from 'react';
import { useState } from 'react';
import { ANIMATIONS, BORDERS, SHADOWS, SPACING, TYPOGRAPHY } from '../../../constants/design';
import { useColors } from '../../../contexts/ThemeContext';

const { Text } = Typography;

export interface SelectableTileProps {
  value: string;
  selected: boolean;
  onChange: (value: string) => void;
  preview: React.ReactNode;
  title: string;
  name: string;
  accentColor?: string;
  accentBg?: string;
}

export const SelectableTile: React.FC<SelectableTileProps> = ({
  value,
  selected,
  onChange,
  preview,
  title,
  name,
  accentColor,
  accentBg,
}) => {
  const tokens = useColors();
  const [isHovered, setIsHovered] = useState(false);

  // Use semantic tokens - fallback to primary colors if accent not provided
  const activeBorderColor = accentColor ?? tokens.primary[500];
  const activeBackgroundColor = accentBg ?? tokens.primary[50];

  // Hover state uses our design tokens
  const hoverBackground = tokens.surface.hover;
  const hoverShadow = SHADOWS.sm;

  return (
    <div
      data-selected={selected ? 'true' : 'false'}
      onClick={() => onChange(value)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onChange(value);
        }
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'space-between',
        cursor: 'pointer',
        padding: `${SPACING.xs} ${SPACING.xs}`,
        borderRadius: BORDERS.radius.md,
        border: selected
          ? `${BORDERS.width.thin} solid ${activeBorderColor}`
          : `${BORDERS.width.thin} solid ${tokens.border.primary}`,
        background: selected
          ? activeBackgroundColor
          : isHovered && !selected
            ? hoverBackground
            : tokens.surface.container,
        transition: `background-color ${ANIMATIONS.duration.normal} ${ANIMATIONS.easing.easeOut}, 
          border-color ${ANIMATIONS.duration.normal} ${ANIMATIONS.easing.easeOut},
          box-shadow ${ANIMATIONS.duration.normal} ${ANIMATIONS.easing.easeOut},
          transform ${ANIMATIONS.duration.fast} ${ANIMATIONS.easing.easeOut}`,
        position: 'relative',
        width: '100%',
        height: '104px',
        boxShadow: selected ? SHADOWS.sm : isHovered && !selected ? hoverShadow : 'none',
        transform: isHovered && !selected ? 'translateY(-1px)' : 'translateY(0)',
        boxSizing: 'border-box',
      }}
    >
      <input
        type="radio"
        name={name}
        value={value}
        checked={selected}
        onChange={() => onChange(value)}
        style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', width: 0, height: 0 }}
      />
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          minHeight: '48px',
          flexShrink: 0,
        }}
      >
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            filter: selected ? 'none' : 'grayscale(0.35)',
            opacity: selected ? 1 : 0.75,
            transition: `filter ${ANIMATIONS.duration.normal} ${ANIMATIONS.easing.easeOut},
              opacity ${ANIMATIONS.duration.normal} ${ANIMATIONS.easing.easeOut}`,
          }}
        >
          {preview}
        </div>
      </div>
      <Text
        style={{
          fontSize: TYPOGRAPHY.fontSize.xs,
          color: selected ? activeBorderColor : tokens.text.secondary,
          fontWeight: selected ? TYPOGRAPHY.fontWeight.semibold : TYPOGRAPHY.fontWeight.normal,
          textAlign: 'center',
          lineHeight: TYPOGRAPHY.lineHeight.snug,
        }}
      >
        {title}
      </Text>
    </div>
  );
};
